import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import iconexcel from "../images/iconexcel.png";
import "../css/dashboard.css";
import { yyyymmdd2ddmmyyyy } from "../Utils/dateconverter";
import backendUrl from "../Utils/backendUrl";

const steps = [
  { colors: ["#ff7e5f", "#feb47b"] },
  { colors: ["#ff512f", "#dd2476"] },
  { colors: ["#8e2de2", "#4a00e0"] },
  { colors: ["#ff512f", "#f09819"] },
];

export default function Dashboard() {
  const {
    userData,
    sharedStartDate,
    setSharedStartDate,
    sharedEndDate,
    setSharedEndDate,
    MIN_DATE,
    today,
  } = useOutletContext();

  const [dashboardtable, setDashboardTable] = useState([""]);
  const [usertype, setUserType] = useState(userData?.usertype);
  const [locationCode, setLocationCode] = useState(userData?.location_code || "");

  // Sync usertype & locationCode if userData changes after async Header fetch
  useEffect(() => {
    if (userData?.usertype !== undefined) {
      setUserType(userData.usertype);
    }
    if (userData?.location_code !== undefined) {
      setLocationCode(userData.location_code || "");
    }
  }, [userData]);


  const [stepLabels, setStepLabels] = useState([]);
  const [lastUpdated, setLastUpdated] = useState("");
  const [stationName, setStationName] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Local picker state (modal only) — always start blank
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");

  // Applied dates come from shared context
  const appliedStartDate = sharedStartDate;
  const appliedEndDate   = sharedEndDate;

  // Sync local picker with shared state each time the modal opens
  // If dates are null/empty, keep boxes blank (do not show default dates)
  useEffect(() => {
    if (showFilter) {
      setStartDate(sharedStartDate || "");
      setEndDate(sharedEndDate || "");
    }
  }, [showFilter, sharedStartDate, sharedEndDate]);


  const getClipPath = () => {
    return "polygon(0 0, 95% 0, 100% 50%, 95% 100%, 0 100%)";
  };

  // ===============================================
  // LOAD TABLE
  // ===============================================


  const loadEsakhya = async ( locationCode, usertype, startDate, endDate) => {
    try {
      document.body.classList.add("opacitypage");

      const response = await axios.get(`${backendUrl}Dashboard`, {
        params: {
          locationCode,
          usertype,
          startDate,
          endDate,
        },
      });

      const data = response.data || [];

      setDashboardTable(data);

      const item = data?.[0];

      if (item) {
        if (usertype === 7) {
          setStepLabels([
            {
              name: item.zone_name,
              code:item.zone_code,
            },
          ]);
        } else if (usertype === 6) {
          setStepLabels([
            {
              name: item.zone_name,
              code : item.zone_code,
            },
            {
              name: item.range_name,
              code: item.range_code,
            },
          ]);
        } else if (usertype === 5) {
          setStepLabels([
            {
              name: item.zone_name,
              code : item.zone_code,
            },
            {
              name: item.range_name,
              code : item.range_code,
            },
            {
              name: item.district_name,
              code : item.district_code,
            },
          ]);
        } else if (usertype === 1) {
          setStepLabels([
            {
              name: item.station_name,
            },
          ]);
        } else if (usertype === 0) {
          setStepLabels([
            {
              name: "Admin",
            },
          ]);
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      // REMOVE ONLY AFTER COMPLETE LOAD
      setTimeout(() => {
        document.body.classList.remove("opacitypage");
      }, 100);
    }
  };

  // load update date
  const loadUpdateDate = ( usertype, locationCode) => {
    axios
      .get(`${backendUrl}loadUpdateDate`, {
        params: {
          locationCode: locationCode,
          usertype: usertype,
        },
      })
      .then((response) => {
        const data = response.data;
        setLastUpdated(data[0].updatetime);
        // console.log(data);
      })
      .catch((error) => {
        console.error("Error fetching dashboard data:", error);
        // Optionally, set error state or show user-friendly message here
      });
  };

  const handleStepClick = (level, item) => {
    // console.log("level and item ", level, item);
    if (level === 7) {
      setUserType(7);

      setLocationCode(item.zone_code);
      
      setStepLabels([{ name: item.zone_name, code: item.zone_code }]);
    } else if (level === 6) {
      setUserType(6);
            
      setLocationCode(item.range_code);

      setStepLabels((prev) => [
        prev[0], // keep zone
        { name: item.range_name, code: item.range_code },
      ]);
    } else if (level === 5) {
      setUserType(5);
      setLocationCode(item.district_code);
      setStepLabels((prev) => [
        prev[0], // zone
        prev[1], // range
        { name: item.district_name, code: item.district_code },
      ]);
    }
  };

  const handleBreadcrumbClick = (step, index) => {

    // console.log(" you have clicked on strip ");
    const loginType = userData.usertype;

    // Restriction logic
    if (loginType === 5 && index < 2) {
      return; // District user cannot go to Range/Zone
    }

    if (loginType === 6 && index < 1) {
      return; // Range user cannot go to Zone
    }

    // ✅ Allowed navigation
    const newSteps = stepLabels.slice(0, index + 1);
    setStepLabels(newSteps);

    // Set usertype based on index
    if (index === 0) setUserType(7);
    if (index === 1) setUserType(6);
    if (index === 2) setUserType(5);

    setLocationCode(step.code);
  };

  useEffect(() => {
    if (userData && stepLabels.length === 0) {
      if (userData.usertype === 0 || userData.usertype === 1) {
        setStepLabels([
          { name: stationName, code: userData.locationCode }, //  stationName
        ]);
      } else {
        setStepLabels([{ name: "Zone", code :userData.locationCode}]);
      }
    }
  }, [userData]);


  // ===============================================
  // LOAD ON PAGE / USER CHANGE
  // ===============================================

  useEffect(() => {
    // Wait until usertype is resolved from userData
    if (usertype === undefined || usertype === null) return;

    setDashboardTable([]);

    if (usertype === 5 || usertype === 1) {
      loadUpdateDate( usertype,locationCode);
    }

    // ONLY applied dates go here
    loadEsakhya(locationCode, usertype, appliedStartDate, appliedEndDate);
  }, [locationCode, usertype, appliedStartDate, appliedEndDate]);

  const handleApply = () => {
    // both dates required
    if ((startDate && !endDate) || (!startDate && endDate)) {
      alert("Please select both start and end dates");
      return;
    }

    // Write to shared context → syncs Home page too
    setSharedStartDate(startDate);
    setSharedEndDate(endDate);
    setShowFilter(false);
  };

  // ===============================================
  // RESET FILTER
  // ===============================================

  const handleStarEndtDate = (e) => {
    const value = e.target.value;

    setStartDate(value);

    // auto reset TO date if invalid
    if (endDate && value > endDate) {
      setEndDate("");
    }
  };


  const handleEndDate = (e) => {
    const value = e.target.value;

    if (value && value > today) {
      alert("End Date cannot be greater than today's date");
      return;
    }

    if (value && startDate && value < startDate) {
      alert("End Date cannot be less than Start Date");
      return;
    }

    setEndDate(value);
  };

  const handleDownloadExcel = async () => {
     if (downloading) return;

    try {
       setDownloading(true);

      const response = await axios.get(`${backendUrl}dashboardexcel`, {
        params: {
          locationCode:locationCode,
          usertype: usertype,
          startDate: appliedStartDate,
          endDate: appliedEndDate,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `E-Sakshya Dashboard ${userData.name}.xlsx`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      
      setDownloading(false);

    } catch (error) {
      console.error("Download failed:", error);
      setDownloading(false);
    }
  };

  return (
    <>
      {/* tag for zone range district and update last date  */}

      <div className="container mt-3">
        <div className="row justify-content-between align-items-center">
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-primary d-flex icon-excel"
              disabled = {downloading}
              onClick={handleDownloadExcel}
            >
              <span>Download</span> <img src={iconexcel} alt="Download" />
            </button>
          </div>
          <div className="col-auto">
            <div className="col-md-1">
              <button
                className="btn btn-warning d-flex align-items-center gap-1"
                onClick={() => setShowFilter(true)}
              >
                <i className="bi bi-calendar3-range-fill"></i>
                <span>Filter</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-0">
        <div className="breadcrumb-container">
          {/* Breadcrumb */}
          <div className="breadcrumb-list">
            {stepLabels.map((step, index) => {
              const loginType = userData.usertype;

              const isDisabled =
                loginType === 1 ||
                (loginType === 5 && index < 2) ||
                (loginType === 6 && index < 1);

              return (
                <div
                  key={index}
                  onClick={() =>
                    !isDisabled && handleBreadcrumbClick(step, index)
                  }
                  className="breadcrumb-arrow position-relative d-flex align-items-center justify-content-center text-white fw-bold"
                  style={{
                    width: "230px",
                    height: "30px",
                    fontSize: "10px",
                    background: `linear-gradient(
                135deg,
                ${steps[index]?.colors[0]},
                ${steps[index]?.colors[1]}
              )`,
                    clipPath: getClipPath(),
                    zIndex: steps.length - index,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  {step.name}
                </div>
              );
            })}
          </div>

          {(usertype === 1 || usertype === 5) && (
            <div className="last-updated-tag">
              ⏱ Last Updated: {lastUpdated}
            </div>
          )}
        </div>
      </div>

      {/* table part  */}
      <div
        className="container table-container mt-0"
        style={{ height: "400px" }}
      >
        <table
          className="table table-dark justify-content-center  text-center"
          id="esakshya-table"
        >
          <thead>
            {!appliedStartDate || !appliedEndDate ? (
              <tr>
                <th colSpan={14}> Esakshya Dashboard Report </th>
              </tr>
            ) : (
              <tr>
                <th colSpan={14}>
                  {" "}
                  Esakshya Dashboard From {yyyymmdd2ddmmyyyy(
                    appliedStartDate,
                  )}{" "}
                  To {yyyymmdd2ddmmyyyy(appliedEndDate)}{" "}
                </th>
              </tr>
            )}
            <tr>
              <th>Sr. No.</th>
              {usertype === 0 && (
                <>
                  <th>Zone</th>
                </>
              )}
              {usertype === 7 && (
                <>
                  <th>Range</th>
                </>
              )}
              {usertype === 6 && <th>District</th>}

              {usertype === 1 && (
                <th>
                  POLICE <br />
                  STATION
                </th>
              )}
              {usertype === 5 && (
                <th>
                  POLICE <br />
                  STATION
                </th>
              )}

              <th>Total FIR</th>
              <th>
                FIR <br />
                WITHOUT <br />
                SID
              </th>
                <th>
                PENDING FIR <br />
                WITHOUT SID
              </th>

              <th>
                COMPLETED FIR <br />
                WITHOUT SID
              </th>
              <th>
                TOTAL
                <br /> SID
                <br /> LINKED
              </th>
              <th>
                PENDING <br />
                FOR FINAL
                <br /> LINKING
              </th>
              <th>
                PENDING
                <br /> FOR
                <br /> LINKING
              </th>
              <th>
                SELF <br />
                LINKED
              </th>
              <th>
                LINKED <br />
                FROM <br />
                OTHER PS
              </th>

              <th>UNIQUE FIR</th>

              <th>% UNIQUE FIR</th>
            </tr>
          </thead>
          <tbody>
            {dashboardtable.length > 0 && dashboardtable[0].name !== null ? (
              dashboardtable.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  {usertype === 0 && (
                    <>
                      <td
                        onClick={() => handleStepClick(7, item)}
                        style={{ cursor: "pointer" }}
                        className="clickable-cell"
                      >
                        {item.zone_name}
                      </td>
                    </>
                  )}
                  {usertype === 7 && (
                    <>
                      <td
                        onClick={() => handleStepClick(6, item)}
                        style={{ cursor: "pointer" }}
                        className="clickable-cell"
                      >
                        {item.range_name}
                      </td>
                    </>
                  )}
                  {usertype === 6 && (
                    <td
                      onClick={() => handleStepClick(5, item)}
                      style={{ cursor: "pointer" }}
                      className="clickable-cell"
                    >
                      {item.district_name}
                    </td>
                  )}
                  {usertype === 1 && <td>{item.station_name}</td>}
                  {usertype === 5 && <td>{item.station_name}</td>}

                  <td>{item.total_fir}</td>
                  <td>{item.fir_without_sid}</td>
                  <td>{item.pending_fir_without_sid}</td>
                  <td>{item.completed_fir_without_sid}</td>
                  <td>{item.total_sid_linked}</td>
                  <td>{item.pfl}</td>
                  <td>{item.pl}</td>
                  <td>{item.ls}</td>
                  <td>{item.lo}</td>
                  <td>{item.unique_sid}</td>
                  <td>{item.per + " %"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={15} className="text-center">
                  Data is not found.
                </td>
              </tr>
            )}
          </tbody>
          {dashboardtable.length > 0 &&
            dashboardtable[0].name !== null &&
            userData.usertype !== 1 && (
              <tfoot className="table-dark sticky-bottom-custom bottom-0">
                <tr>
                  <td
                    colSpan={
                      userData.usertype === 0
                        ? 2
                        : userData.usertype === 7
                          ? 2
                          : userData.usertype === 6
                            ? 2
                            : userData.usertype === 5
                              ? 2
                              : 1
                    }
                  >
                    Total
                  </td>

                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.total_fir || 0),
                      0,
                    )}
                  </td>

                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.fir_without_sid || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.pending_fir_without_sid || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.completed_fir_without_sid || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.total_sid_linked || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.pfl || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.pl || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.ls || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.lo || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {dashboardtable.reduce(
                      (acc, cur) => acc + parseInt(cur.unique_sid || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {(() => {
                      const sum_sid = dashboardtable.reduce(
                        (acc, cur) => acc + parseInt(cur.unique_sid || 0),
                        0,
                      );
                      const sum_fir = dashboardtable.reduce(
                        (acc, cur) => acc + parseInt(cur.total_fir || 0),
                        0,
                      );
                      if (sum_fir === 0) return "-";
                      return ((sum_sid / sum_fir) * 100).toFixed(2) + "%";
                    })()}
                  </td>
                </tr>
              </tfoot>
            )}
        </table>
      </div>

      {showFilter && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{
              background: "rgba(0,0,0,0.5)",
              zIndex: 1040,
            }}
            onClick={() => setShowFilter(false)}
          ></div>

          {/* Modal */}
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{
              zIndex: 1050,
              width: "400px",
            }}
          >
            <div className="card shadow-lg">
              <div className="card-header d-flex text-center bg-warning justify-content-between align-items-center">
                <h5 className="mb-0 ">Filter FIR-SID</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFilter(false)}
                ></button>
              </div>

              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    min={MIN_DATE}
                    max={endDate || today}
                    onChange={handleStarEndtDate}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    min={startDate}
                    max={today}
                    onChange={handleEndDate}
                  />
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      // Only clear the picker boxes — does NOT apply until user clicks Apply
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    Reset
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      handleApply();
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
